import {z} from 'zod'
import {captureException} from '@sentry/react'
import axios, {parseAxiosPromise} from '@/axiosInstance.ts'
import {PaginationResponse} from '@/types'
import {
    ActivityReport,
    ActivityReportCounters,
    ActivityReportSector,
    ActivityReportStatus
} from '@/features/accesses/activity-report/types'

/* Counters preview — all params required (FR-009) */
export type HttpGetActivityReportCountersParams = {
    startDate: string
    endDate: string
    sector: ActivityReportSector
}

export const httpGetActivityReportCounters = (params: HttpGetActivityReportCountersParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('v1/activity-reports/counters', {params}),
        responseShape: ActivityReportCounters,
        onZodError: captureException
    })

/* History (paginated, cross-user) */
export type HttpGetActivityReportHistoryParams = {
    sector?: ActivityReportSector
    status?: ActivityReportStatus
    page?: number
    pageSize?: number
}

export const HttpGetActivityReportHistoryResponse = z.object({
    items: z.array(ActivityReport),
    meta: PaginationResponse
})
export type HttpGetActivityReportHistoryResponse = z.infer<typeof HttpGetActivityReportHistoryResponse>

export const httpGetActivityReportHistory = (params: HttpGetActivityReportHistoryParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('v1/activity-reports', {params}),
        responseShape: HttpGetActivityReportHistoryResponse,
        onZodError: captureException
    })

/* Generate report (async) → 202 with the record + embedded export job */
export type HttpCreateActivityReportPayload = {
    startDate: string
    endDate: string
    sector: ActivityReportSector
}

export const httpCreateActivityReport = (payload: HttpCreateActivityReportPayload) =>
    parseAxiosPromise({
        axiosPromise: axios.post('v1/activity-reports', payload),
        responseShape: ActivityReport,
        onZodError: captureException
    })

/* Re-generate file (async) → 202, same shape as create */
export const httpRegenerateActivityReport = ({id}: {id: string}) =>
    parseAxiosPromise({
        axiosPromise: axios.post(`v1/activity-reports/${id}/regenerate`),
        responseShape: ActivityReport,
        onZodError: captureException
    })

/* Mark as billed → updated record */
export const httpBillActivityReport = ({id}: {id: string}) =>
    parseAxiosPromise({
        axiosPromise: axios.post(`v1/activity-reports/${id}/bill`),
        responseShape: ActivityReport,
        onZodError: captureException
    })

/* Delete (only while pending) → 204 No Content */
export const httpDeleteActivityReport = ({id}: {id: string}): Promise<null> =>
    axios.delete(`v1/activity-reports/${id}`).then(() => null)
